const INCLUDE_IMAGES = true;
const SKIP_EXISTING_PERSONAS = false;
const MIN_SERVICE_DESCRIPTION_LENGTH = 10;
const MIN_SEGMENT_NAME_LENGTH = 2;
const MAX_SEGMENT_NAME_LENGTH = 20;
const MIN_SEGMENT_DESCRIPTION_LENGTH = 10;
const MAX_UI_PERSONA_COUNT = 3;

const isDevelopment = () => process.env.NODE_ENV !== 'production';

const compactObject = (value) =>
  Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined && entry !== null && entry !== ''));

const normalizeLocaleObject = (locale) => ({
  country: String(locale.country || 'KR').toUpperCase(),
  language: String(locale.language || 'ko').toLowerCase(),
  ...(locale.region ? { region: String(locale.region) } : {}),
});

export function toPersonaLocale(value) {
  if (value && typeof value === 'object') {
    return normalizeLocaleObject(value);
  }
  const [language, country] = String(value || 'ko-KR').split('-');
  return normalizeLocaleObject({ country: country || 'KR', language: language || 'ko' });
}

export function buildGenerationPayload({ createTab, generationForm, segments, locale }) {
  const normalizedLocale = toPersonaLocale(locale);

  if (createTab === 'segment') {
    const segmentInputs = (segments || [])
      .filter((segment) => String(segment.name || '').trim() && String(segment.definition || '').trim())
      .map((segment, index) => {
        const name = (String(segment.name || '').trim() || `세그먼트 ${index + 1}`).slice(0, MAX_SEGMENT_NAME_LENGTH);
        const description = String(segment.definition || '').trim();
        return {
          id: String(segment.id || `segment-${index + 1}`),
          name,
          description,
          criteria: String(segment.filters || '').trim(),
          targetCount: Math.max(1, Number(segment.count) || 1),
        };
      });

    return {
      sourceType: 'segment_based',
      segmentInputs,
      totalCount: Math.max(1, segmentInputs.reduce((sum, segment) => sum + segment.targetCount, 0)),
      locale: normalizedLocale,
      includeImages: INCLUDE_IMAGES,
      skipExistingPersonas: SKIP_EXISTING_PERSONAS,
    };
  }

  return {
    sourceType: 'service_based',
    serviceDescription: String(generationForm?.serviceDescription || '').trim(),
    targetAudience: String(generationForm?.targetAudience || '').trim(),
    totalCount: Math.max(1, Number(generationForm?.count) || 1),
    locale: normalizedLocale,
    includeImages: INCLUDE_IMAGES,
    skipExistingPersonas: SKIP_EXISTING_PERSONAS,
  };
}

export function validateGenerationInput({ createTab, generationForm, segments }) {
  const errors = [];

  if (createTab === 'segment') {
    const enteredSegments = (segments || []).filter((segment) => (
      String(segment?.name || '').trim() ||
      String(segment?.definition || '').trim() ||
      String(segment?.filters || '').trim()
    ));
    if (enteredSegments.length === 0) {
      errors.push('세그먼트를 최소 1개 이상 입력해 주세요.');
    }

    let totalCount = 0;
    enteredSegments.forEach((segment, index) => {
      const label = `세그먼트 ${index + 1}`;
      const name = String(segment?.name || '').trim();
      const definition = String(segment?.definition || '').trim();
      const count = Number(segment?.count) || 0;
      if (name.length < MIN_SEGMENT_NAME_LENGTH) {
        errors.push(`${label} 명칭은 ${MIN_SEGMENT_NAME_LENGTH}자 이상 입력해 주세요.`);
      }
      if (definition.length < MIN_SEGMENT_DESCRIPTION_LENGTH) {
        errors.push(`${label} 정의 및 퍼소나 특성은 ${MIN_SEGMENT_DESCRIPTION_LENGTH}자 이상 입력해 주세요.`);
      }
      if (count < 1) {
        errors.push(`${label} 퍼소나 수는 1명 이상이어야 합니다.`);
      }
      totalCount += Math.max(0, count);
    });

    if (totalCount < 1) {
      errors.push('생성할 퍼소나 수를 1명 이상으로 설정해 주세요.');
    }
    if (totalCount > MAX_UI_PERSONA_COUNT) {
      errors.push(`한 번에 최대 ${MAX_UI_PERSONA_COUNT}명까지 생성할 수 있습니다.`);
    }
    return errors;
  }

  const serviceDescription = String(generationForm?.serviceDescription || '').trim();
  const count = Number(generationForm?.count) || 0;
  if (serviceDescription.length < MIN_SERVICE_DESCRIPTION_LENGTH) {
    errors.push(`서비스 정의 및 주요 기능을 ${MIN_SERVICE_DESCRIPTION_LENGTH}자 이상 입력해 주세요.`);
  }
  if (count < 1) {
    errors.push('생성할 퍼소나 수를 1명 이상으로 설정해 주세요.');
  }
  if (count > MAX_UI_PERSONA_COUNT) {
    errors.push(`한 번에 최대 ${MAX_UI_PERSONA_COUNT}명까지 생성할 수 있습니다.`);
  }
  return errors;
}

export function buildSegmentSuggestionPayload({ context, locale, maxSegments }) {
  return compactObject({
    context: String(context || '').trim(),
    locale: toPersonaLocale(locale),
    maxSegments,
  });
}

export function normalizeSegmentSuggestions(segments = [], maxTotalCount = 3) {
  return (segments || []).reduce((accumulator, segment, index) => {
    const usedCount = accumulator.reduce((sum, current) => sum + Math.max(1, Number(current.count) || 1), 0);
    const remainingCount = Math.max(0, Number(maxTotalCount) || 0) - usedCount;
    if (remainingCount < 1) {
      return accumulator;
    }

    const name = String(segment?.name || '').trim();
    const definition = String(segment?.description || segment?.definition || '').trim();
    if (!name || !definition) {
      return accumulator;
    }

    accumulator.push({
      id: segment?.id || `segment-suggested-${Date.now()}-${index}`,
      name: name.slice(0, MAX_SEGMENT_NAME_LENGTH),
      definition,
      filters: String(segment?.criteria || segment?.filters || '').trim(),
      count: Math.min(remainingCount, Math.max(1, Number(segment?.targetCount || segment?.target_count || segment?.count) || 1)),
    });

    return accumulator;
  }, []);
}

export function buildSaveSourceData(requestPayload = {}, generationResult = {}) {
  const common = compactObject({
    generationMetadata: generationResult.generationMetadata || {},
    segments: Array.isArray(generationResult.segments) ? generationResult.segments : [],
    telecomServiceUsageContextReferences: Array.isArray(generationResult.telecomServiceUsageContextReferences)
      ? generationResult.telecomServiceUsageContextReferences
      : [],
    generationMode: generationResult.generationMode,
    durationMs: generationResult.durationMs,
    tokenUsage: generationResult.tokenUsage,
  });

  if (requestPayload.sourceType === 'segment_based') {
    return {
      segmentInputs: requestPayload.segmentInputs || [],
      ...common,
    };
  }

  return {
    serviceDescription: requestPayload.serviceDescription || '',
    targetAudience: requestPayload.targetAudience || '',
    ...common,
  };
}

export function buildGeneratedPersonasSavePayload({
  selectedPersonas,
  generationResult,
  folderId,
  fallbackLocale,
}) {
  const requestPayload = generationResult?.requestPayload || {};
  return {
    personas: selectedPersonas,
    sourceType: generationResult?.sourceType || requestPayload.sourceType || 'service_based',
    sourceData: buildSaveSourceData(requestPayload, generationResult || {}),
    locale: requestPayload.locale || toPersonaLocale(fallbackLocale),
    folderId: folderId ? Number(folderId) : null,
    generateImages: true,
  };
}

const firstText = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number') return String(value);
    if (Array.isArray(value)) {
      const joined = value
        .map((entry) => firstText(entry))
        .filter(Boolean)
        .join(', ');
      if (joined) return joined;
    }
    if (typeof value === 'object') {
      const direct = firstText(value.summary, value.description, value.value, value.name);
      if (direct) return direct;
      const nestedText = Object.values(value)
        .map((entry) => firstText(entry))
        .filter(Boolean)
        .slice(0, 2)
        .join(' / ');
      if (nestedText) return nestedText;
    }
  }
  return '';
};

export function getGeneratedPersonaProfile(persona) {
  const profile = persona?.profile || {};
  const telecomDimensions = persona?.telecomBehaviorDimensions || profile.telecomBehaviorDimensions;

  return {
    gender: firstText(persona?.gender, profile.gender),
    age: firstText(persona?.age, profile.age),
    job: firstText(persona?.title, profile.job, profile.occupation, persona?.occupation),
    personality: firstText(persona?.personality, profile.personality, persona?.personality_description) ||
      '성격 정보가 아직 생성되지 않았습니다.',
    biography: firstText(persona?.biography, profile.biography, profile.history) ||
      '이력 정보가 아직 생성되지 않았습니다.',
    behavior: firstText(
      persona?.behaviours,
      persona?.behavior_pattern,
      persona?.behavior_patterns,
      profile.behaviours,
      profile.behavior_pattern,
      profile.behavior_patterns,
      persona?.lifestyle,
      profile.lifestyle
    ) || '행동 패턴 정보가 아직 생성되지 않았습니다.',
    telecomBehavior: firstText(telecomDimensions) || '통신 행동 특성이 아직 생성되지 않았습니다.',
  };
}

export function warnForGeneratedPersonaGaps(personas = []) {
  if (!isDevelopment()) return;
  personas.forEach((persona, index) => {
    const missing = [
      ['biography', persona?.biography],
      ['personality', persona?.personality],
      ['telecomBehaviorDimensions', persona?.telecomBehaviorDimensions],
      ['imageUrl', persona?.imageUrl || persona?.image_url],
    ]
      .filter(([, value]) => !value)
      .map(([field]) => field);

    if (missing.length) {
      console.warn('[persona] Generated persona preview is missing fields', {
        index,
        name: persona?.name,
        missing,
      });
    }
  });
}
